import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Stethoscope, Activity, ShieldCheck, Phone, MapPin, Clock, Award, Users } from "lucide-react";
import { HOSPITAL, SERVICES, STATS, DOCTORS } from "../lib/hospitalData";

const ICONS = { "heart-pulse": HeartPulse, stethoscope: Stethoscope, activity: Activity, default: ShieldCheck };

export default function Home() {
  return (
    <main data-testid="page-home">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-cream grain">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-crimson/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-brand-navy/10 blur-3xl pointer-events-none" />

        <div className="container-x relative pt-10 pb-16 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-navy/15 text-[10.5px] sm:text-xs font-semibold tracking-wide text-brand-navy-deep">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-crimson animate-heart-pulse" />
              <span className="leading-tight">75-BED CARDIAC & SUPER-SPECIALTY HOSPITAL · GODHRA</span>
            </div>
            <h1 className="font-display mt-5 text-[34px] leading-[1.05] sm:text-5xl lg:text-6xl font-bold text-brand-navy-deep">
              Where <span className="text-brand-crimson italic">Every Heartbeat</span> Matters.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 max-w-2xl leading-relaxed">
              Bringing the region’s first full-time interventional cardiology and cardiothoracic surgery services to Godhra — with compassionate, evidence-based, and accessible super-specialty care for every section of society.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-book-appointment" className="btn-primary">
                Book an Appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${HOSPITAL.phoneRaw}`} data-testid="hero-call-emergency" className="btn-outline">
                <Phone className="w-4 h-4" /> 24×7 Emergency
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-6">
              {STATS.map((s, i) => (
                <div key={s.label} className={`reveal reveal-${i+1}`}>
                  <div className="font-display text-[28px] sm:text-4xl font-bold text-brand-navy-deep">{s.value}</div>
                  <div className="text-[10.5px] sm:text-xs uppercase tracking-wider text-slate-600 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 reveal reveal-2 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(19,60,121,0.55)]" style={{ background: "linear-gradient(135deg, hsl(217 75% 19%), hsl(217 73% 27%))" }}>
              <div className="absolute inset-0 grain opacity-50" />
              {/* ECG SVG */}
              <svg viewBox="0 0 600 400" className="absolute inset-0 w-full h-full">
                <defs>
                  <linearGradient id="ecg-grad" x1="0" x2="1">
                    <stop offset="0" stopColor="#C61C23" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#C61C23" />
                    <stop offset="1" stopColor="#C61C23" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  className="ecg-line"
                  d="M0,200 L120,200 L150,200 L165,140 L180,260 L200,200 L240,200 L260,180 L280,220 L300,200 L380,200 L400,90 L420,310 L440,200 L520,200 L540,180 L560,220 L600,200"
                  fill="none" stroke="url(#ecg-grad)" strokeWidth="2.5"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-brand-crimson/30 blur-2xl animate-heart-pulse" />
                  <HeartPulse className="relative w-32 h-32 sm:w-44 sm:h-44 text-white/95 animate-heart-pulse" strokeWidth={1.2} />
                </div>
              </div>
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-8 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl p-4 pr-6 shadow-xl border border-border max-w-[260px]">
              <div className="w-12 h-12 rounded-full bg-brand-crimson/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-brand-crimson" />
              </div>
              <div>
                <div className="font-semibold text-sm text-brand-navy-deep">National Standards</div>
                <div className="text-xs text-slate-500">Evidence-based clinical excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24" data-testid="why-choose-us">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-brand-crimson font-semibold">Why Godhra Heart Hospital</div>
            <h2 className="font-display text-[30px] sm:text-4xl md:text-5xl font-bold text-brand-navy-deep mt-3 leading-tight">
              Advanced cardiac care, finally <span className="italic text-brand-crimson">close to home.</span>
            </h2>
          </div>
          <div className="mt-10 md:mt-14 grid sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: HeartPulse, title: "Region's First Cath Lab", desc: "Full-time interventional cardiology with primary PCI, angioplasty and structural heart procedures — saving the golden hour, locally." },
              { icon: ShieldCheck, title: "Affordable & Accessible", desc: "Empanelled with government health schemes including free care for the underprivileged and tribal communities of Panchmahal." },
              { icon: Users, title: "Patient-First Care", desc: "Compassion, dignity and transparency at the centre of every interaction — from OPD to discharge and beyond." },
              { icon: Activity, title: "10+ Super-Specialties", desc: "Cardiology, CTVS, ENT, Oncology, General Medicine & Surgery, Maxillofacial, Critical Care — all under one roof." },
              { icon: Clock, title: "24×7 Emergency", desc: "Round-the-clock ICU, CICU and emergency departments equipped to handle the most critical cases." },
              { icon: Award, title: "National Clinical Standards", desc: "Skilled professionals, advanced technology and evidence-based protocols ensuring patient safety and quality." },
            ].map((f, i) => (
              <div key={i} className={`card-lift bg-white border border-border rounded-2xl p-7 reveal reveal-${(i%5)+1}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-navy/8 flex items-center justify-center text-brand-navy-deep">
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-xl text-brand-navy-deep mt-5">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 md:py-24 bg-brand-cream relative overflow-hidden" data-testid="home-services">
        <div className="container-x relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 md:gap-6 mb-10 md:mb-12">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-brand-crimson font-semibold">Our Services</div>
              <h2 className="font-display text-[30px] sm:text-4xl md:text-5xl font-bold text-brand-navy-deep mt-3">Comprehensive Specialties</h2>
            </div>
            <Link to="/services" className="btn-outline self-start md:self-end">View All Services <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.slice(0,6).map((s, i) => {
              const Icon = ICONS[s.icon] || ICONS.default;
              return (
                <Link to={`/services#${s.id}`} key={s.id} className={`card-lift bg-white border border-border rounded-2xl p-6 group reveal reveal-${(i%5)+1}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-lg bg-brand-crimson/10 flex items-center justify-center text-brand-crimson">
                      <Icon className="w-5 h-5" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-brand-crimson transition-colors" />
                  </div>
                  <h3 className="font-display text-lg text-brand-navy-deep mt-4">{s.name}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{s.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="py-16 md:py-24" data-testid="home-doctors">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs tracking-[0.25em] uppercase text-brand-crimson font-semibold">Meet Our Directors</div>
            <h2 className="font-display text-[30px] sm:text-4xl md:text-5xl font-bold text-brand-navy-deep mt-3">Led by Specialists Who Care</h2>
          </div>
          <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
            {DOCTORS.map((d, i) => (
              <div key={d.id} className={`card-lift bg-white border border-border rounded-3xl overflow-hidden reveal reveal-${i+1}`}>
                <div className="grid grid-cols-1 sm:grid-cols-5">
                  <div className="sm:col-span-2 bg-brand-cream relative aspect-[4/3] sm:aspect-auto sm:min-h-[280px]">
                    <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover object-top" />
                  </div>
                  <div className="sm:col-span-3 p-6 md:p-7">
                    <div className="text-[10.5px] tracking-[0.2em] uppercase text-brand-crimson font-bold">{d.title}</div>
                    <h3 className="font-display text-xl md:text-2xl text-brand-navy-deep mt-2">{d.name}</h3>
                    <div className="text-sm text-slate-600 mt-1.5 font-medium">{d.qualifications}</div>
                    <p className="text-sm text-slate-600 mt-3 md:mt-4 leading-relaxed line-clamp-4">{d.bio}</p>
                    <Link to="/doctors" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-navy-deep mt-4 md:mt-5 hover:text-brand-crimson">
                      Read more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy relative overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="container-x py-14 md:py-20 grid md:grid-cols-2 gap-8 md:gap-10 items-center relative">
          <div>
            <h2 className="font-display text-[30px] sm:text-4xl md:text-5xl font-bold leading-tight">
              Need an appointment or emergency consult?
            </h2>
            <p className="mt-4 md:mt-5 text-white/80 text-base md:text-lg max-w-xl">
              Our team is available round-the-clock for emergencies and elective consultations. Book online or call us directly.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">Book Appointment</Link>
              <a href={`tel:${HOSPITAL.phoneRaw}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors font-semibold text-sm">
                <Phone className="w-4 h-4" /> {HOSPITAL.phone}
              </a>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur">
            <div className="flex items-center gap-2 text-brand-crimson mb-3"><MapPin className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-bold">Visit Us</span></div>
            <div className="font-display text-2xl">{HOSPITAL.address.line1}</div>
            <div className="text-white/80 mt-1">{HOSPITAL.address.city}, {HOSPITAL.address.district}, {HOSPITAL.address.state} – {HOSPITAL.address.pincode}</div>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60">OPD Hours</div>
                <div className="font-semibold mt-1">Mon – Sat · 9 AM – 8 PM</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60">Emergency</div>
                <div className="font-semibold mt-1">24×7 · Always Open</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
